// Login page wired su mockup Login.html — sfondo earth tone gradient,
// brand block MB serif, card 420px con shadow, field con icon prefix +
// password toggle, remember-me checkbox, forgot password flow, error
// banner con attempts_remaining counter dal backend.

import { useState, type FormEvent } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, forgotPassword, login } from '../lib/api'
import { hasTokens, setTokens } from '../lib/auth'
import { ME_QUERY_KEY } from '../hooks/useAuth'

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (hasTokens()) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [showPwd, setShowPwd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotMsg, setForgotMsg] = useState<string | null>(null)
  const [forgotSubmitting, setForgotSubmitting] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setAttemptsRemaining(null)
    setSubmitting(true)
    try {
      const tokens = await login(email, password, rememberMe)
      setTokens(tokens.access_token, tokens.refresh_token)
      qc.removeQueries({ queryKey: ME_QUERY_KEY })
      // Full reload invece di SPA navigate: RootLayout legge hasTokens()
      // solo al primo mount. SPA navigate non re-mounta -> dashboard
      // rende dentro .center-page wrapper (no sidebar / header rotto).
      // window.location.replace evita anche entry history del login.
      window.location.replace('/')
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        // Extract attempts_remaining dal detail object backend.
        const detail = (err.detail as { detail?: unknown })?.detail
        if (detail && typeof detail === 'object' && 'attempts_remaining' in detail) {
          const rem = (detail as { attempts_remaining: unknown }).attempts_remaining
          if (typeof rem === 'number') setAttemptsRemaining(rem)
        }
      } else {
        setError('Errore di rete')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function onForgotSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setForgotSubmitting(true)
    setForgotMsg(null)
    try {
      const res = await forgotPassword(forgotEmail || email)
      setForgotMsg(res.message)
    } catch (err) {
      // Anti-enumeration: even on error mostra messaggio generico.
      setForgotMsg(
        'Se l\'email corrisponde a un account, riceverai istruzioni. ' +
          'Per assistenza immediata contatta IT.',
      )
      void err
    } finally {
      setForgotSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-wrap">
        {/* BRAND BLOCK */}
        <div className="brand-block">
          <div className="brand-mark-lg">MB</div>
          <div className="brand-name">MB Aste</div>
          <div className="brand-sub">AGL Roma EUR · Superpowers</div>
        </div>

        {/* CARD */}
        <div className="login-card">
          {showForgot ? (
            <>
              <h1>Recupero password</h1>
              <div className="h1-sub">
                Inserisci la tua email aziendale per richiedere il reset.
              </div>
              {forgotMsg && (
                <div
                  className="err-banner"
                  style={{
                    background: 'oklch(96% 0.04 145)',
                    borderColor: 'oklch(85% 0.08 145)',
                    color: 'oklch(35% 0.12 145)',
                  }}
                >
                  <span className="ic" aria-hidden>
                    ✓
                  </span>
                  <div className="txt">{forgotMsg}</div>
                </div>
              )}
              <form onSubmit={onForgotSubmit}>
                <div className="field">
                  <label>Email aziendale</label>
                  <div className={`input ${forgotEmail ? 'has-value' : ''}`}>
                    <IconMail />
                    <input
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="nome.cognome@agl-aste.it"
                      disabled={forgotSubmitting}
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-login"
                  disabled={forgotSubmitting || !forgotEmail}
                  style={{ marginTop: 'var(--sp-3)' }}
                >
                  {forgotSubmitting ? (
                    <>
                      <span className="spinner" /> Invio…
                    </>
                  ) : (
                    'Invia richiesta reset'
                  )}
                </button>
                <button
                  type="button"
                  className="link-back"
                  onClick={() => {
                    setShowForgot(false)
                    setForgotMsg(null)
                  }}
                  style={{
                    marginTop: 'var(--sp-3)',
                    background: 'transparent',
                    border: 0,
                    color: 'var(--ink-3)',
                    fontSize: 'var(--fs-12)',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  ← Torna al login
                </button>
              </form>
            </>
          ) : (
            <>
              <h1>Accedi</h1>
              <div className="h1-sub">
                Inserisci le tue credenziali per accedere al gestionale
              </div>

              {error && (
                <div className="err-banner" role="alert">
                  <IconAlert />
                  <div className="txt">
                    <b>{error}</b>
                    {attemptsRemaining != null && attemptsRemaining > 0 && (
                      <span className="tries">
                        {attemptsRemaining} tentativ
                        {attemptsRemaining === 1 ? 'o' : 'i'} riman
                        {attemptsRemaining === 1 ? 'e' : 'enti'}
                      </span>
                    )}
                    {attemptsRemaining === 0 && (
                      <span className="tries">⛔ account bloccato 30min</span>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={onSubmit}>
                <div className="field">
                  <label>Email aziendale</label>
                  <div className={`input ${email ? 'has-value' : ''}`}>
                    <IconMail />
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome.cognome@agl-aste.it"
                      disabled={submitting}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="field">
                  <label>Password</label>
                  <div className={`input ${password ? 'has-value' : ''}`}>
                    <IconLock />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      className="pwd-toggle"
                      onClick={() => setShowPwd((v) => !v)}
                      title={showPwd ? 'Nascondi password' : 'Mostra password'}
                      tabIndex={-1}
                    >
                      <IconEye open={!showPwd} />
                    </button>
                  </div>
                </div>

                <div className="row-line">
                  <label className="checkbox-line">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{ display: 'none' }}
                    />
                    <span className={`cb ${rememberMe ? 'checked' : ''}`} />
                    Resta connesso 30 giorni
                  </label>
                  <button
                    type="button"
                    className="link-forgot"
                    onClick={() => {
                      setShowForgot(true)
                      setForgotEmail(email)
                    }}
                  >
                    Password dimenticata?
                  </button>
                </div>

                <button
                  type="submit"
                  className="btn-login"
                  disabled={submitting || !email || !password}
                >
                  {submitting ? (
                    <>
                      <span className="spinner" /> Accesso…
                    </>
                  ) : (
                    <>
                      Accedi <span aria-hidden>›</span>
                    </>
                  )}
                </button>

                <div className="access-note">
                  <IconLockSmall />
                  Accesso riservato al personale AGL · 2FA disponibile
                </div>
              </form>
            </>
          )}

          <div className="card-foot">
            <span className="ver">v1.0.0 — build 24.05.26</span>
            <span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Privacy
              </a>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Termini
              </a>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Supporto
              </a>
            </span>
          </div>
        </div>

        <div className="footnote">
          Problemi di accesso?{' '}
          <a href="mailto:it@agl-aste.it">Contatta IT support</a> o chiama{' '}
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              color: 'var(--ink-2)',
            }}
          >
            06 4477 2200
          </span>
        </div>
      </div>
    </div>
  )
}

// SVG icons inline (no library) — coerenti con design system mockup.
function IconMail() {
  return (
    <svg
      className="ic"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 7 9-7" />
    </svg>
  )
}

function IconLock() {
  return (
    <svg
      className="ic"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

function IconLockSmall() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: 4, verticalAlign: -1, color: 'var(--ink-4)' }}
      aria-hidden
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

function IconEye({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 2l20 20" />
      <path d="M6.7 6.7C3.6 8.9 2 12 2 12s4 7 10 7c2 0 3.7-.7 5.1-1.7" />
      <path d="M22 12s-1.6-3-4.6-5.1" />
      <path d="M10.5 10.5a2 2 0 0 0 3 3" />
    </svg>
  )
}

function IconAlert() {
  return (
    <svg
      className="ic"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16v.5" />
    </svg>
  )
}
